import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LineIcon from '../../components/LineIcon';
import WorkerSignupLayout, {
  NAVY, AMBER, WHITE, GREEN, FONT_HEAD, FONT_BODY, StepPills,
} from './WorkerSignupLayout';

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

export default function WorkerStep1() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    password: '', confirmPassword: '',
    phone: '', zip: '', agreed: false, ageConfirmed: false,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [existingUserId, setExistingUserId] = useState(null);
  const [saveError, setSaveError] = useState('');

  const [googleHover, setGoogleHover] = useState(false);

  // Reached via "Back" after already creating the account (or a Google signup that
  // routes back here) -- reload what's already saved instead of showing blank fields.
  useEffect(() => {
    const loadExisting = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      const { data: worker } = await supabase.from('workers')
        .select('first_name, last_name, email, phone, zip_code')
        .eq('id', userId).maybeSingle();
      if (worker) {
        setExistingUserId(userId);
        setForm(p => ({
          ...p,
          firstName: worker.first_name || '',
          lastName: worker.last_name || '',
          email: worker.email || '',
          phone: worker.phone || '',
          zip: worker.zip_code || '',
          // Reaching this step with an account already means these were already agreed to.
          agreed: true,
          ageConfirmed: true,
        }));
      }
    };
    loadExisting();
  }, []);

  const handleGoogleSignup = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/callback',
        queryParams: { prompt: 'select_account' },
      }
    })
    if (error) alert(error.message)
  }

  const handleContinue = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      alert(t('union.signup.step1.requiredFieldsAlert'))
      return
    }
    // Password fields are only ever populated on a fresh signup -- reaching this step via
    // Back after the account already exists (see loadExisting above) intentionally leaves
    // them blank and skips auth.signUp entirely below, so don't gate on them here.
    if (!existingUserId) {
      if (!form.password || !form.confirmPassword) {
        alert(t('union.signup.step1.requiredFieldsAlert'))
        return
      }
      if (form.password.length < 8) {
        alert(t('union.signup.step1.passwordLengthAlert'))
        return
      }
      if (form.password !== form.confirmPassword) {
        alert(t('employer.signup.step1.passwordMismatchAlert'))
        return
      }
    }
    if (!form.agreed) {
      alert(t('worker.signup.step1.agreeAlert'))
      return
    }
    if (!form.ageConfirmed) {
      alert(t('worker.signup.step1.ageAlert'))
      return
    }

    let userId = existingUserId;

    if (!userId) {
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            first_name: form.firstName,
            last_name: form.lastName,
          }
        }
      })

      if (error) {
        alert(error.message)
        return
      }

      userId = data?.user?.id
      if (!userId) {
        alert(t('worker.signup.step1.noUserAlert'))
        return
      }
      // The login exists from here on. Remember it so that if the workers save below fails,
      // pressing Continue again retries that save instead of calling signUp a second time
      // (which would fail with "already registered" and strand the account).
      setExistingUserId(userId)
    }

    // Only advance once the saved workers row comes back -- the error used to be captured and
    // then ignored, sending a worker with no workers row on to Step 2.
    setSaveError('')
    const { data: saved, error: upsertError } = await supabase.from('workers').upsert({
      id: userId,
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.phone,
      zip_code: form.zip,
    }, { onConflict: 'id' }).select('id')
    if (upsertError || saved?.length !== 1) { setSaveError(t('worker.signup.saveFailed')); return }
    navigate('/worker/signup/personal')
  }

  return (
    <WorkerSignupLayout
      step={1}
      tipText={t('worker.signup.step1.tip')}
      onContinue={handleContinue}
    >
      {/* Eyebrow */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('worker.signup.step1.eyebrow')}
      </div>

      <StepPills current={1} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('worker.signup.step1.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('worker.signup.step1.subtitle')}
        </p>

        {/* Google signup */}
        <button
          onClick={handleGoogleSignup}
          onMouseEnter={() => setGoogleHover(true)}
          onMouseLeave={() => setGoogleHover(false)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '10px', padding: '12px 16px', marginBottom: '20px',
            backgroundColor: googleHover ? '#F5F6F8' : WHITE,
            border: '1px solid #D1D5DB', borderRadius: '8px',
            cursor: 'pointer', fontFamily: FONT_BODY,
            fontSize: '0.9375rem', fontWeight: 500, color: '#1F2937',
            transition: 'background-color 0.15s ease',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"/>
            <path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2c-7.6 0-14.2 4.1-17.7 10.2z" transform="translate(0,2)"/>
            <path fill="#FBBC05" d="M24 46c5.9 0 10.9-2 14.6-5.4l-6.8-5.6C29.8 36.6 27.1 37 24 37c-6.1 0-10.7-3.1-11.8-7.5l-7 5.4C8.2 41.9 15.5 46 24 46z" transform="translate(0,-2)"/>
            <path fill="#EA4335" d="M44.5 20H24v8.5h11.8c-1 3-3.2 5.4-6.2 6.9l6.8 5.6C40.3 37.4 44.5 31.2 44.5 24c0-1.3-.2-2.7-.5-4z"/>
          </svg>
          {t('worker.signup.step1.googleButton')}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
          <span style={{ fontSize: '0.8125rem', color: 'rgba(13,27,42,0.4)', flexShrink: 0 }}>{t('worker.signup.step1.or')}</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
        </div>

        {/* First + Last */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.firstNameLabel')}</label>
            <input style={inp} placeholder="Maria" value={form.firstName} onChange={e => set('firstName', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.lastNameLabel')}</label>
            <input style={inp} placeholder="Gonzalez" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
          </div>
        </div>

        {/* Email */}
        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step1.emailLabel')}</label>
          <input style={inp} type="email" placeholder="you@email.com" value={form.email} onChange={e => set('email', e.target.value)} />
        </div>

        {/* Password + Confirm */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
          </div>
        </div>

        {/* Phone + ZIP */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.phoneLabel')}</label>
            <input style={inp} type="tel" placeholder="(312) 555-0100" value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.zipLabel')}</label>
            <input style={inp} placeholder="60601" value={form.zip} onChange={e => set('zip', e.target.value)} />
          </div>
        </div>

        {/* Terms */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.85rem', color: 'rgba(13,27,42,0.7)', lineHeight: 1.5 }}>
          <input
            type="checkbox"
            checked={form.agreed}
            onChange={e => set('agreed', e.target.checked)}
            style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span>
            {t('worker.signup.step1.agreePrefix')}{' '}
            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('common.termsOfService')}</a>
            {' '}{t('worker.signup.step1.agreeAnd')}{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('common.privacyPolicy')}</a>
            {t('worker.signup.step1.agreeSuffix') && ` ${t('worker.signup.step1.agreeSuffix')}`}
          </span>
        </label>

        {/* Age confirmation */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.85rem', color: 'rgba(13,27,42,0.7)', lineHeight: 1.5, marginTop: '12px' }}>
          <input
            type="checkbox"
            checked={form.ageConfirmed}
            onChange={e => set('ageConfirmed', e.target.checked)}
            style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span>{t('worker.signup.step1.ageConfirmLabel')}</span>
        </label>
      </div>

      {/* Trust banner */}
      <div style={{
        backgroundColor: `rgba(29,158,117,0.1)`,
        border: `1px solid rgba(29,158,117,0.28)`,
        borderRadius: '10px', padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: '10px',
        fontSize: '0.85rem', fontWeight: 600, color: GREEN,
      }}>
        <LineIcon name="lock" />
        {t('worker.signup.step1.trustBanner')}
      </div>
      {saveError && (
        <p role="alert" data-testid="signup-save-error" style={{ margin: '1rem 0 0', fontSize: '0.9rem', fontWeight: 600, color: '#B91C1C', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          {saveError}
        </p>
      )}
    </WorkerSignupLayout>
  );
}
